// @flow
import type { FluxStandardAction } from 'modules/helper';
import type { GalleryImageType } from 'types/common';
import type {
  ActiveProjectFilters,
  NeighbourhoodFilters,
  ProjectFilters,
} from 'types/filter';
import type {
  ConstructionGalleryType,
  Neighbourhood,
  NeighbourhoodTypes,
  Project,
  ProjectAnnouncement,
  CompletedProjects,
} from 'types/project';
import { ProjectActionTypes } from './actionTypes';

export type ProjectState = {
  announcements: Array<ProjectAnnouncement> | null,
  gallery: Array<GalleryImageType> | null,
  projects: Object<Project> | null,
  neighbourhood: Array<Neighbourhood> | null,
  filters: ProjectFilters,
  activeFilters: ActiveProjectFilters,
  neighbourhoodTypes: Array<NeighbourhoodTypes>,
  activeNeighbourhoodFilter: Object<NeighbourhoodTypes>,
  constructionGallery: Object<ConstructionGalleryType>,
  completedProjects: Object<CompletedProjects>,
  projectBrochure: Object<CompletedProjects>,
  loading: {
    announcements: boolean,
    gallery: boolean,
    projects: boolean,
    neighbourhood: boolean,
    constructionGallery: boolean,
    completedProjects: boolean,
    projectBrochure: boolean,
  },
  error: {
    announcements: string | null,
    gallery: string | null,
    projects: string | null,
    neighbourhood: string | null,
    constructionGallery: string | null,
    completedProjects: string | null,
    projectBrochure: string | null,
  },
};

const getInitialActiveFilterState = (): ActiveProjectFilters => {
  return {
    location: [],
    projectStatus: [],
    propertyType: [],
    beds: undefined,
    size: [],
    year: [],
    area: [],
    range: [],
  };
};

const initialState: ProjectState = {
  announcements: null,
  gallery: null,
  projects: null,
  neighbourhood: null,
  filters: null,
  activeFilters: null,
  neighbourhoodTypes: null,
  activeNeighbourhoodFilter: null,
  constructionGallery: null,
  completedProjects: null,
  projectBrochure: null,
  loading: {
    announcements: false,
    gallery: false,
    projects: false,
    neighbourhood: false,
    constructionGallery: false,
    completedProjects: false,
    projectBrochure: false,
  },
  error: {
    announcements: null,
    gallery: null,
    projects: false,
    neighbourhood: false,
    constructionGallery: null,
    completedProjects: null,
    projectBrochure: null,
  },
};

const toggleArrayItem = (value: number, arr: Array<number>): Array<number> => {
  const filterSet = new Set(arr);
  if (filterSet.has(value)) {
    filterSet.delete(value);
  } else {
    filterSet.add(value);
  }
  return Array.from(filterSet);
};

const updateActiveFilters = (
  payload: { type: string, value: number },
  filters: ActiveProjectFilters
): ActiveProjectFilters => {
  const newFilters = { ...filters };
  const value = payload.value;
  switch (payload.type) {
    case 'beds':
      newFilters.beds = value;
      break;
    case 'size':
      newFilters.size = value;
      break;
    case 'year':
      newFilters.year = value;
      break;
    case 'area':
      newFilters.area = value;
      break;
    case 'location':
      newFilters.location = toggleArrayItem(value, newFilters.location);
      break;
    case 'projectStatus':
      newFilters.projectStatus = toggleArrayItem(
        value,
        newFilters.projectStatus
      );
      break;
    case 'propertyType':
      newFilters.propertyType = toggleArrayItem(value, newFilters.propertyType);
      break;
    default:
      break;
  }

  return newFilters;
};

export const ProjectReducer = (
  state: ProjectState = initialState,
  action: FluxStandardAction
) => {
  const { type, payload, error } = action;

  switch (type) {
    case 'PAGE_UNMOUNTED':
      return initialState;
    // Announcement
    case ProjectActionTypes.ANNOUNCEMENTS.FETCH:
      return {
        ...state,
        loading: { ...state.loading, announcements: true },
        error: { ...state.error, announcements: null },
      };
    case ProjectActionTypes.ANNOUNCEMENTS.SUCCESS:
      return {
        ...state,
        announcements: payload,
        loading: { ...state.loading, announcements: false },
      };
    case ProjectActionTypes.ANNOUNCEMENTS.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, announcements: false },
        error: { ...state.error, announcements: error },
      };
    // Gallery
    case ProjectActionTypes.GALLERY.FETCH:
      return {
        ...state,
        loading: { ...state.loading, gallery: true },
        error: { ...state.error, gallery: null },
      };
    case ProjectActionTypes.GALLERY.SUCCESS:
      return {
        ...state,
        gallery: payload,
        loading: { ...state.loading, gallery: false },
      };
    case ProjectActionTypes.GALLERY.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, gallery: false },
        error: { ...state.error, gallery: error },
      };
    // Project Browser
    case ProjectActionTypes.PROJECTS.FETCH:
      return {
        ...state,
        loading: { ...state.loading, projects: true },
        error: { ...state.error, projects: null },
      };
    case ProjectActionTypes.PROJECTS.SUCCESS:
      return {
        ...state,
        projects: payload,
        loading: { ...state.loading, projects: false },
      };
    case ProjectActionTypes.PROJECTS.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, projects: false },
        error: { ...state.error, projects: error },
      };
    // Project filters
    case ProjectActionTypes.PROJECT_FILTERS.INITIALIZE:
      return {
        ...state,
        filters: payload,
        activeFilters: Object.assign({}, getInitialActiveFilterState()),
      };
    case ProjectActionTypes.PROJECT_FILTERS.TOGGLE_FILTER:
      const activeFilters = updateActiveFilters(payload, state.activeFilters);
      return {
        ...state,
        activeFilters: { ...activeFilters },
      };
    case ProjectActionTypes.PROJECT_FILTERS.CLEAR_FILTERS:
      return {
        ...state,
        activeFilters: Object.assign({}, getInitialActiveFilterState()),
      };
    // Project Neighbourhood Guide
    case ProjectActionTypes.NEIGHBOURHOOD_GUIDE.FETCH:
      return {
        ...state,
        loading: { ...state.loading, neighbourhood: true },
        error: { ...state.error, neighbourhood: null },
      };
    case ProjectActionTypes.NEIGHBOURHOOD_GUIDE.SUCCESS:
      return {
        ...state,
        neighbourhood: payload,
        loading: { ...state.loading, neighbourhood: false },
      };
    case ProjectActionTypes.NEIGHBOURHOOD_GUIDE.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, neighbourhood: false },
        error: { ...state.error, neighbourhood: payload.error },
      };
    case ProjectActionTypes.NEIGHBOURHOOD_FILTERS.INITIALIZE_FILTER:
      return {
        ...state,
        neighbourhoodTypes: payload,
      };
    case ProjectActionTypes.NEIGHBOURHOOD_FILTERS.TOGGLE_FILTER:
      return {
        ...state,
        activeNeighbourhoodFilter: payload,
      };
    // Construction Gallery
    case ProjectActionTypes.CONSTRUCTION_GALLERY.FETCH:
      return {
        ...state,
        loading: { ...state.loading, constructionGallery: true },
        error: { ...state.error, constructionGallery: null },
      };
    case ProjectActionTypes.CONSTRUCTION_GALLERY.SUCCESS:
      return {
        ...state,
        constructionGallery: payload,
        loading: { ...state.loading, constructionGallery: false },
      };
    case ProjectActionTypes.CONSTRUCTION_GALLERY.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, constructionGallery: false },
        error: { ...state.error, constructionGallery: error },
      };
    // COMPLETED PROJECTS
    case ProjectActionTypes.COMPLETED_PROJECTS.FETCH:
      return {
        ...state,
        loading: { ...state.loading, completedProjects: true },
        error: { ...state.error, completedProjects: null },
      };
    case ProjectActionTypes.COMPLETED_PROJECTS.SUCCESS:
      return {
        ...state,
        completedProjects: payload,
        loading: { ...state.loading, completedProjects: false },
      };
    case ProjectActionTypes.COMPLETED_PROJECTS.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, completedProjects: false },
        error: { ...state.error, completedProjects: error },
      };
    // PROJECT DETAILS SECTION
    case ProjectActionTypes.PROJECT_BROCHURE.FETCH:
      return {
        ...state,
        loading: { ...state.loading, projectBrochure: true },
        error: { ...state.error, projectBrochure: null },
      };
    case ProjectActionTypes.PROJECT_BROCHURE.SUCCESS:
      return {
        ...state,
        projectBrochure: payload,
        loading: { ...state.loading, projectBrochure: false },
      };
    case ProjectActionTypes.PROJECT_BROCHURE.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, projectBrochure: false },
        error: { ...state.error, projectBrochure: error },
      };
    default:
      return state;
  }
};
